import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import './NewDashboard.scss';
import './DashboardTabs.scss';
import { RegulationContext } from '../../../App';
import DashboardTabs from './DashboardTabs';
import EsgPerformanceTab from './EsgPerformanceTab';
import GoalsAndKpisTab from './GoalsAndKpisTab';
import ESGAnalytics from '../ESGAnalytics/ESGAnalytics';
import ESGAnalyticsView from '../ESGAnalytics/ESGAnalyticsView';
import DoubleMaterialityTab from './DoubleMaterialityTab';
import { useSearchParams } from 'react-router-dom';
import NewWidgetLibraryPanel from './NewWidgetLibraryPanel';
import ChartMaximizeModal from './ChartMaximizeModal';
import TableViewModal from './TableViewModal';
import ModernChartCard from './ModernChartCard';
import {
  getCompanyInfoFromStorage,
  getDefaultSelectableYear,
  getMaxSelectableYear,
} from '../../../core/utilities/assessmentPeriodUtils';

const VALID_DASHBOARD_CATEGORIES = ['Environmental', 'Social', 'Governance'];

function getInitialDashboardCategory(propCategory) {
  if (propCategory && VALID_DASHBOARD_CATEGORIES.includes(propCategory)) {
    return propCategory;
  }
  if (typeof localStorage === 'undefined') {
    return 'Environmental';
  }
  const saved = localStorage.getItem('selectedCategory');
  return VALID_DASHBOARD_CATEGORIES.includes(saved) ? saved : 'Environmental';
}

function getInitialDashboardYear(propYear) {
  if (propYear !== undefined && propYear !== null) {
    return propYear;
  }
  const companyInfo = getCompanyInfoFromStorage();
  const maxSelectableYear = getMaxSelectableYear();
  const currentYearValue = new Date().getFullYear();
  const startYearValue = currentYearValue - 5;
  const defaultYear = getDefaultSelectableYear(companyInfo);

  if (typeof localStorage === 'undefined') {
    return defaultYear;
  }
  const savedYear = localStorage.getItem('selectedYear');
  if (savedYear) {
    const parsed = parseInt(savedYear, 10);
    if (
      !Number.isNaN(parsed) &&
      parsed >= startYearValue &&
      parsed <= maxSelectableYear
    ) {
      return parsed;
    }
  }
  return defaultYear;
}

const NewDashboard = ({
  // Props for embedded mode (from NewGHGOverview)
  selectedYear: propSelectedYear,
  selectedCategory: propSelectedCategory,
  scopeGraphs,
  layoutConfig,
  scope,
  layout,
  isEmbedded = false, // Flag to determine if this is embedded mode
  selectedScope: propSelectedScope,
  onScopeChange: propOnScopeChange,
  onYearChange: propOnYearChange,
  onWidgetChange, // Callback to notify parent when widgets are added/removed
  breadcrumbParentLabel
}) => {
  const { t, i18n } = useTranslation();
  const { glbRegulation } = useContext(RegulationContext);
  // Standalone: start in loading state so dashboard children (e.g. EsgPerformanceTab) are not mounted
  // on the first paint and then unmounted when the simulated loading overlay appears — that caused
  // duplicate getCompanyGraphs calls on first load.
  const [loading, setLoading] = useState(() => !isEmbedded);
  const [error, setError] = useState(null);
  const [data, setData] = useState();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'esg';
  
  // Widget management state
  const [widgetsPanelDisplay, setWidgetsPanelDisplay] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() =>
    getInitialDashboardCategory(propSelectedCategory)
  );
  const [selectedYear, setSelectedYear] = useState(() =>
    getInitialDashboardYear(propSelectedYear)
  );
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger to refresh EsgPerformanceTab
  const [maximizedChart, setMaximizedChart] = useState(null);
  const [maximizedRenderedOn, setMaximizedRenderedOn] = useState(null);
  const [chartShowLabels, setChartShowLabels] = useState({});
  
  // Scope management for embedded mode
  const [selectedScope, setSelectedScope] = useState(propSelectedScope || 'ALL');
  
  // KPI Filter state management
  const [isKpiFilterVisible, setIsKpiFilterVisible] = useState(false);
  
  // Table view modal state
  const [tableViewModal, setTableViewModal] = useState({
    open: false,
    chartData: null,
    chartConfig: null,
    title: '',
    year: null
  });

  // Sync propSelectedYear with selectedYear state when prop changes
  useEffect(() => {
    if (propSelectedYear !== undefined && propSelectedYear !== selectedYear) {
      setSelectedYear(propSelectedYear);
    }
  }, [propSelectedYear]);

  // Simulate loading state - only for standalone mode (initial loading is already true; clear after delay)
  useEffect(() => {
    if (!isEmbedded) {
      const timer = setTimeout(() => {
        setLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isEmbedded]);


  // Handle widget library open
  const handleOpenWidgetLibrary = () => {
    setWidgetsPanelDisplay(true);
  };

  // Handle widget library close
  const handleCloseWidgetLibrary = () => {
    setWidgetsPanelDisplay(false);
  };

  // Handle widget addition
  const handleAddWidget = () => {
    // This will be called when a widget is added from the library
    // setWidgetsPanelDisplay(false);
    console.log('handleAddWidget');   
    
    // Trigger refresh of EsgPerformanceTab to fetch updated widgets
    setRefreshTrigger(prev => prev + 1);
    
    // In embedded mode, notify parent to refresh scope graphs
    if (isEmbedded && onWidgetChange) {
      onWidgetChange();
    }
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  // Handle year change
  const handleYearChange = (year) => {
    if (isEmbedded && propOnYearChange) {
      propOnYearChange(year);
    } else {
      setSelectedYear(year);
    }
  };

  // Handle scope change for embedded mode
  const handleScopeChange = (scope) => {
    if (isEmbedded && propOnScopeChange) {
      propOnScopeChange(scope);
    } else {
      setSelectedScope(scope);
    }
  };

  // Handle KPI filter toggle
  const handleToggleKpiFilter = () => {
    setIsKpiFilterVisible(!isKpiFilterVisible);
  };

  const getChartShowLabels = (chartId) => chartShowLabels[chartId] ?? false;

  const handleChartShowLabelsChange = (chartId, show) => {
    setChartShowLabels((prev) => ({ ...prev, [chartId]: show }));
  };

  // When opening the modal, set renderedOn to Date.now()
  const handleMaximizeChart = (chart) => {
    setMaximizedChart(chart);
    setMaximizedRenderedOn(Date.now());
  };

  // Handle table view modal
  const handleTableViewModal = (chartData, chartConfig, title) => {
    setTableViewModal({
      open: true,
      chartData,
      chartConfig,
      title,
      year: selectedYear
    });
  };

  // Handle close table view modal
  const handleCloseTableViewModal = () => {
    setTableViewModal({
      open: false,
      chartData: null,
      chartConfig: null,
      title: '',
      year: null
    });
  };

  if (loading) {
    return (
      <div className="new-dashboard">
        <div className="new-dashboard__loading">
          <div className="loading-spinner"></div>
          <p>{t("loadingDashboard.label")}</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="new-dashboard">
        <div className="new-dashboard__error">
          <p>{t("errorLoadingDashboard.label")}</p>
          <button onClick={() => window.location.reload()}>{t("retry.label")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="new-dashboard">
      {/* Maximize Modal at top level */}
      <ChartMaximizeModal
        open={!!maximizedChart}
        onClose={() => {
          setMaximizedChart(null);
          setMaximizedRenderedOn(null);
        }}
        chartConfig={maximizedChart}
        chartYear={selectedYear}
        chartTitle={maximizedChart?.title}
        standardId={glbRegulation?.id}
        showLabels={maximizedChart ? getChartShowLabels(maximizedChart.id) : false}
        onShowLabelsChange={(show) => {
          if (maximizedChart?.id) {
            handleChartShowLabelsChange(maximizedChart.id, show);
          }
        }}
      >
        {maximizedChart && (
          <ModernChartCard
            config={maximizedChart}
            year={selectedYear}
            renderedOn={maximizedRenderedOn}
            title={maximizedChart.title}
            type={maximizedChart.type}
            data={maximizedChart.data}
            onMaximize={() => {
              setMaximizedChart(null);
              setMaximizedRenderedOn(null);
            }}
            isMaximizedView={true}
            isModalSized={true}
            bare={true}
            height={420}
            showLabels={getChartShowLabels(maximizedChart.id)}
            onShowLabelsChange={(show) => handleChartShowLabelsChange(maximizedChart.id, show)}
          />
        )}
      </ChartMaximizeModal>

      {/* Table View Modal */}
      <TableViewModal
        open={tableViewModal.open}
        onClose={handleCloseTableViewModal}
        chartData={tableViewModal.chartData}
        chartConfig={tableViewModal.chartConfig}
        title={tableViewModal.title}
        year={tableViewModal.year}
      />
      {/* <div className="new-dashboard__header">
        <h1 className="new-dashboard__title">Dashboard</h1>
        <p className="new-dashboard__subtitle">Monitor your ESG performance and sustainability metrics</p>
      </div> */}
      
      <div className={`new-dashboard__content ${widgetsPanelDisplay ? 'new-dashboard__content--widget-panel-open' : ''}`}>
        {/* Show full tabs in standalone mode */}
        {!isEmbedded && (
          <DashboardTabs 
            onOpenWidgetLibrary={handleOpenWidgetLibrary}
            selectedCategory={selectedCategory}
            selectedYear={selectedYear}
            onCategoryChange={handleCategoryChange}
            onYearChange={handleYearChange}
            onToggleKpiFilter={handleToggleKpiFilter}
            isKpiFilterVisible={isKpiFilterVisible}
            isEmbedded={false}
          />
        )}
        
        {/* Show Overview tab in embedded mode */}
        {isEmbedded && (
          <DashboardTabs 
            onOpenWidgetLibrary={handleOpenWidgetLibrary}
            selectedCategory={selectedCategory}
            selectedYear={propSelectedYear || selectedYear}
            onCategoryChange={handleCategoryChange}
            onYearChange={handleYearChange}
            onToggleKpiFilter={handleToggleKpiFilter}
            isKpiFilterVisible={isKpiFilterVisible}
            isEmbedded={true}
            selectedScope={propSelectedScope || selectedScope}
            onScopeChange={handleScopeChange}
            breadcrumbParentLabel={breadcrumbParentLabel}
          />
        )}
        
        <div className="new-dashboard__main-content">
          <div className="new-dashboard__tab-content">
            {/* In embedded mode, always show EsgPerformanceTab with custom props */}
            {isEmbedded ? (
              <EsgPerformanceTab
                data={data}
                selectedCategory={selectedCategory}
                selectedYear={selectedYear}
                refreshTrigger={refreshTrigger}
                onMaximizeChart={handleMaximizeChart}
                onTableViewModal={handleTableViewModal}
                getChartShowLabels={getChartShowLabels}
                onChartShowLabelsChange={handleChartShowLabelsChange}
                scopeGraphs={scopeGraphs}
                layoutConfig={layoutConfig}
                scope={scope}
                layout={layout}
                isEmbedded={isEmbedded}
              />
            ) : (
              <>
                {activeTab === 'esg' && (
                  <EsgPerformanceTab
                    data={data}
                    selectedCategory={selectedCategory}
                    selectedYear={selectedYear}
                    refreshTrigger={refreshTrigger}
                    onMaximizeChart={handleMaximizeChart}
                    onTableViewModal={handleTableViewModal}
                    getChartShowLabels={getChartShowLabels}
                    onChartShowLabelsChange={handleChartShowLabelsChange}
                  />
                )}
                {activeTab === 'goals' && (
                  <GoalsAndKpisTab 
                    isKpiFilterVisible={isKpiFilterVisible}
                    onToggleKpiFilter={handleToggleKpiFilter}
                  />
                )}
                {activeTab === 'compare' && (
                  <ESGAnalyticsView />
                )}
                {/* {activeTab === 'double' && <DoubleMaterialityTab />} */}
              </>
            )}
          </div>

          {/* Widget Library Panel - Inline Sidebar */}
          <div className={`new-dashboard__widget-sidebar ${widgetsPanelDisplay ? 'new-dashboard__widget-sidebar--visible' : ''}`}>
            {widgetsPanelDisplay && (
              <NewWidgetLibraryPanel
                category={selectedCategory}
                year={selectedYear}
                onClose={handleCloseWidgetLibrary}
                onAddWidget={handleAddWidget}
                // Pass scope and layout parameters for embedded mode
                scope={scope}
                layout={layout}
                isEmbedded={isEmbedded}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewDashboard; 